import { ReactNode } from "react";
import { cn } from "web-design-system";
import { PortfolioReviewAction } from "creso-core/types";
import { formatAsPercentage } from "creso-core/utils";
import { AmcLogo } from "@core/components/logo/amc-logo";
import { RankedItem } from "./report-data";

export function ReportPageTitle({
  title,
  aside,
  subsection,
}: {
  title: string;
  aside?: ReactNode;
  // A heading nested under another page title.
  subsection?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span
        className={cn("font-semibold", subsection ? "text-base" : "text-lg")}
      >
        {title}
      </span>
      {aside}
    </div>
  );
}

export function ReportStats({
  stats,
  className,
}: {
  stats: { label: string; value: ReactNode }[];
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-6 rounded-lg border border-border bg-card px-8 py-5",
        className
      )}
    >
      {stats.map(({ label, value }) => (
        <div key={label} className="flex flex-col gap-2">
          <span className="text-sm text-muted-foreground">{label}</span>
          <span className="text-lg font-semibold">{value}</span>
        </div>
      ))}
    </div>
  );
}

export function TotalPill({ label, value }: { label: string; value: string }) {
  return (
    <span className="rounded-lg bg-card px-4 py-2.5 text-sm font-semibold">
      {label}: <span className="ml-2">{value}</span>
    </span>
  );
}

const ACTION_PILL: Record<PortfolioReviewAction, string> = {
  HOLD: "bg-tag-blue text-tag-blue-foreground",
  SWITCH: "bg-tag-yellow text-tag-yellow-foreground",
  EXIT: "bg-tag-red text-tag-red-foreground",
  ADD: "bg-tag-green text-tag-green-foreground",
};

const ACTION_LABEL: Record<PortfolioReviewAction, string> = {
  HOLD: "Hold",
  SWITCH: "Switch",
  EXIT: "Exit",
  ADD: "Add",
};

export function ActionPill({ action }: { action: PortfolioReviewAction }) {
  return (
    <span
      className={cn(
        "inline-flex w-25 justify-center rounded-lg py-2 text-sm font-medium",
        ACTION_PILL[action]
      )}
    >
      {ACTION_LABEL[action]}
    </span>
  );
}

export function SignedReturn({ value }: { value: number }) {
  return (
    <span className={value >= 0 ? "text-constructive" : "text-destructive"}>
      {value >= 0 ? "↑" : "↓"}
      {formatAsPercentage(Math.abs(value))}
    </span>
  );
}

// Current portfolio on the left, the portfolio after the review on the right.
export function BeforeAfter({
  before,
  after,
}: {
  before: ReactNode;
  after: ReactNode;
}) {
  return (
    <div className="grid grid-cols-2 gap-6">
      {[
        { label: "Current portfolio", content: before },
        { label: "After review", content: after },
      ].map(({ label, content }) => (
        <div key={label} className="flex flex-col gap-4">
          <span className="text-sm font-medium text-muted-foreground">
            {label}
          </span>
          {content}
        </div>
      ))}
    </div>
  );
}

// A list of names with a bar sized to each share, as on the exposure pages.
export function RankedList({
  items,
  withLogo,
}: {
  items: (Pick<RankedItem, "key" | "name" | "pct"> & {
    amcCode?: string | null;
  })[];
  withLogo?: boolean;
}) {
  const max = Math.max(...items.map((i) => i.pct), 1);
  return (
    <div className={cn("flex flex-col", withLogo ? "gap-4" : "gap-3")}>
      {items.map((item) => (
        <div key={item.key} className="flex items-center gap-4 text-sm">
          {withLogo ? (
            <div className="relative flex min-w-0 flex-1 items-center gap-2 p-0.5">
              {/* The largest share spans 60% of the row; the bar always
                  covers the logo. */}
              <div
                className="absolute inset-y-0 left-0 min-w-9 rounded-lg bg-slate-100 dark:bg-secondary"
                style={{ width: `${(item.pct / max) * 60}%` }}
              />
              <AmcLogo
                amc_code={item.amcCode ?? null}
                className="relative rounded-md"
              />
              <span className="relative truncate text-xs">{item.name}</span>
            </div>
          ) : (
            <>
              <span className="w-32 shrink-0 truncate">{item.name}</span>
              <div className="flex-1">
                <div
                  className="h-6 rounded-md bg-linear-to-r from-muted to-border"
                  style={{ width: `${(item.pct / max) * 100}%` }}
                />
              </div>
            </>
          )}
          <span
            className={cn(
              "w-12 shrink-0 text-right",
              withLogo ? "font-semibold" : "text-muted-foreground"
            )}
          >
            {formatAsPercentage(item.pct, "-", 0)}
          </span>
        </div>
      ))}
    </div>
  );
}
